import {
  useState,
  useContext,
  Suspense,
  useEffect,
  useMemo,
  useDeferredValue,
  memo,
  useCallback,
} from 'react'
import { ContentLayout, PageLayout } from '../../layout'
import {
  AgxTextInput,
  AgxRow,
  AgxColumn,
  AgxLabel,
  AgxHeader,
  AgxToast,
  AgxFilterableTableHeader,
  TableColumn,
  TableHeaderFilter,
  AgxPartnershipIcon,
  AgxColours,
} from '@urbanx/agx-ui-components'
import { PartnershipDetailsPartnershipLevel } from '@urbanx/agx.rex.client'
import { useNavigate } from 'react-router-dom'
import { Pages } from 'constants/menu'
import { PlaceHolderImage } from 'components'
import { AgencyContext } from 'contexts'
import Popup from '../../components/Popup/Popup'
import { useMutation } from '@tanstack/react-query'
import { CreateAgency } from 'services'
import { AgxToastState } from 'types/commonTypes'

import './agencies.scss'
import slugify from 'slugify'
import { AuthContext } from '@urbanx/agx.auth.client'
import {
  AgencyModel,
  LicenceDetailsState,
  useGetAllAgencies,
  useInvalidateUseGetAllAgencies,
} from '@urbanx/agx.agencies.client'
import AgenciesIcon from 'components/sidebar/icons/agencies-icon'
import AgenciesHeader from './AgenciesHeader'

const LOCATION_FILTER = 'location_filter'
const PARTNERSHIP_FILTER = 'partnership_filter'

const initialColumns = [
  {
    id: '',
    name: '',
    options: [],
    width: '76px',
  },
  {
    id: 'agencyName',
    name: 'Agency',
    options: [],
    width: '50%',
  },
  {
    id: LOCATION_FILTER,
    name: 'Location',
    width: '25%',
    options: [
      {
        label: 'ACT',
        value: 'ACT',
        selected: false,
        default: false,
      },
      {
        label: 'NSW',
        value: 'NSW',
        selected: false,
        default: false,
      },
      {
        label: 'NT',
        value: 'NT',
        selected: false,
        default: false,
      },
      {
        label: 'QLD',
        value: 'QLD',
        selected: false,
        default: false,
      },
      {
        label: 'SA',
        value: 'SA',
        selected: false,
        default: false,
      },
      {
        label: 'TAS',
        value: 'TAS',
        selected: false,
        default: false,
      },
      {
        label: 'WA',
        value: 'WA',
        selected: false,
        default: false,
      },
      {
        label: 'VIC',
        value: 'VIC',
        selected: false,
        default: false,
      },
    ],
  },
  {
    id: PARTNERSHIP_FILTER,
    name: 'Partnership',
    justifyRight: true,
    options: [
      {
        label: 'Bronze',
        value: PartnershipDetailsPartnershipLevel.Bronze,
        selected: false,
        default: false,
      },
      {
        label: 'Silver',
        value: PartnershipDetailsPartnershipLevel.Silver,
        selected: false,
        default: false,
      },
      {
        label: 'Gold',
        value: PartnershipDetailsPartnershipLevel.Gold,
        selected: false,
        default: false,
      },
      {
        label: 'Platinum',
        value: PartnershipDetailsPartnershipLevel.Platinum,
        selected: false,
        default: false,
      },
      {
        label: 'XPartner',
        value: PartnershipDetailsPartnershipLevel.XPartner,
        selected: false,
        default: false,
      },
    ],
  },
]

const AgenciesPage = () => {
  const { userAccount, getAuthToken } = useContext(AuthContext)
  const [shouldResetFilters, setShouldResetFilters] = useState(false)
  const [columns] = useState<TableColumn[]>(initialColumns)
  const [locationFilters, setLocationFilters] = useState<TableHeaderFilter[]>(
    []
  )
  const [partnershipFilters, setPartnershipFilters] = useState<
    TableHeaderFilter[]
  >([])
  const [, setSelectedAgency] = useContext(AgencyContext)
  const [showPopup, setShowPopup] = useState(false)

  const [searchText, setSearchText] = useState('')
  const deferredSearchText = useDeferredValue(searchText)
  const isSearching = searchText !== deferredSearchText

  const [agencyName, setAgencyName] = useState('')
  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  })

  const navigate = useNavigate()

  const { data: agencies, isPending: isPendingAgencies } = useGetAllAgencies()
  const invalidateGetAllAgencies = useInvalidateUseGetAllAgencies()

  const { mutate: createAgency } = useMutation({
    mutationFn: CreateAgency,
    onSuccess: async (agencyId) => {
      await invalidateGetAllAgencies()
      navigate(`/${Pages.AGENCIES}/${agencyId}/agency-details/`)
      updateToastState({
        color: 'success',
        message: 'Agency added successfully',
        open: true,
      })
    },
    onError: () => {
      updateToastState({
        color: 'error',
        message: 'Error adding agency',
        open: true,
      })
    },
  })

  const onAgencyClicked = useCallback(
    (agency: AgencyModel) => {
      setSelectedAgency(agency)
      navigate({
        pathname: `/${Pages.AGENCIES}/${agency.id}/${Pages.CAMPAIGNS}`,
      })
    },
    [setSelectedAgency, navigate]
  )

  const addNewAgency = () => {
    setShowPopup(true)
  }

  const createNewAgency = () => {
    createAgency({ name: agencyName, getAuthToken })
    setShowPopup(false)
  }

  const filteredAgencies = useMemo(() => {
    if (!agencies || agencies.length === 0) return []

    let filteredAgencies: AgencyModel[] = [...agencies]

    const searchTextToFilterBy = deferredSearchText.toLowerCase().trim()

    if (searchTextToFilterBy.length > 0) {
      filteredAgencies = agencies.filter((agency) =>
        agency?.name?.toLowerCase().includes(searchTextToFilterBy)
      )
    }

    const selectedLocationFilters = locationFilters
      .filter((f) => f.selected)
      .map((f) => f.value as LicenceDetailsState)

    // filter by state filter
    if (selectedLocationFilters.length > 0) {
      filteredAgencies = filteredAgencies.filter((agency) =>
        agency?.states?.some((state) =>
          selectedLocationFilters.includes(
            state as unknown as LicenceDetailsState
          )
        )
      )
    }

    const selectedPartnershipLevelFilters = partnershipFilters
      .filter((f) => f.selected)
      .map((f) => f.value)

    //filter by partnership level
    if (selectedPartnershipLevelFilters.length > 0) {
      filteredAgencies = filteredAgencies.filter(
        (agency) =>
          agency.partnershipDetails?.partnershipLevel != null &&
          selectedPartnershipLevelFilters.includes(
            agency.partnershipDetails.partnershipLevel.toString()
          )
      )
    }

    return filteredAgencies
  }, [deferredSearchText, agencies, locationFilters, partnershipFilters])

  useEffect(() => {
    setSelectedAgency(undefined)

    //set defaultFilters
    const locationColumn = columns.find((c) => c.id === LOCATION_FILTER)
    if (locationColumn)
      setLocationFilters(locationColumn.options.filter((o) => o.selected))

    const partnershipColumn = columns.find((c) => c.id === PARTNERSHIP_FILTER)
    if (partnershipColumn)
      setPartnershipFilters(partnershipColumn.options.filter((o) => o.selected))
  }, [])

  const applyFilters = (filters: {
    id: string
    value: TableHeaderFilter[]
  }) => {
    if (filters.id === LOCATION_FILTER) setLocationFilters(filters.value)
    if (filters.id === PARTNERSHIP_FILTER) setPartnershipFilters(filters.value)
  }

  const userName = useMemo(() => {
    return [userAccount?.firstName, userAccount?.lastName]
      .filter((namePart) => namePart != null && namePart.length > 0)
      .join(' ')
  }, [userAccount])

  return (
    <PageLayout
      agentName={userName}
      primaryPageTitle="Agencies"
      pageIcon={
        <div className="iconContainer">
          <AgenciesIcon />
        </div>
      }
      header={
        <AgenciesHeader
          totalCount={filteredAgencies.length}
          searchText={searchText}
          onSearchTextChange={setSearchText}
          onAddNewAgency={addNewAgency}
        />
      }
    >
      <ContentLayout>
        <AgxToast selector="#agxToast" toastState={toastState} />
        <AgxColumn largeGap>
          {isPendingAgencies ? (
            <Suspense>
              <AgxLabel extraClasses="errorTextStyle__dashboard">
                Loading Agencies...
              </AgxLabel>
            </Suspense>
          ) : (
            <AgxRow extraClasses="agenciesTable__dashboard">
              <table
                width={'100%'}
                style={{
                  opacity: isSearching ? 0.6 : 1,
                  transition: 'opacity 0.2s ease-in-out',
                }}
              >
                <thead>
                  <AgxFilterableTableHeader
                    columns={columns}
                    shouldResetFilters={shouldResetFilters}
                    setShouldResetFilters={setShouldResetFilters}
                    onFilterChange={applyFilters}
                  />
                </thead>
                <tbody>
                  {filteredAgencies.map((a, index) => (
                    <AgencyRow
                      key={index}
                      index={index}
                      agency={a}
                      onAgencyClicked={onAgencyClicked}
                    />
                  ))}
                </tbody>
              </table>
            </AgxRow>
          )}
        </AgxColumn>

        {showPopup ? (
          <Popup
            title="Create new agency"
            isOpen={showPopup}
            onClose={() => setShowPopup(false)}
            actionButtons={[
              {
                id: 'create-agency',
                title: 'Create',
                isPrimary: true,
                clickHandler: createNewAgency,
              },
              {
                id: 'cancel-create-agency',
                title: 'Cancel',
                clickHandler: () => setShowPopup(false),
              },
            ]}
            size={{ widthInPX: 400 }}
          >
            <div>
              <AgxTextInput
                id="agencyName"
                label="Agency name"
                noOptionalLabel
                stretch
                onInputValueChange={({ value }: { value: string }) =>
                  setAgencyName(value)
                }
              />
            </div>
          </Popup>
        ) : (
          <div></div>
        )}
      </ContentLayout>
    </PageLayout>
  )
}

const AgencyRow = memo(
  ({
    agency,
    index,
    onAgencyClicked,
  }: {
    agency: AgencyModel
    index: number
    onAgencyClicked: (agency: AgencyModel) => void
  }) => {
    const handleClick = useCallback(() => {
      onAgencyClicked(agency)
    }, [agency, onAgencyClicked])

    return (
      <tr
        key={`Agency-${index}-${agency.id}`}
        className="tableRowStyle__dashboard"
        onClick={handleClick}
      >
        <td width={'76px'}>
          {agency.logoUrl ? (
            <div className="imageContainer">
              <img
                src={agency.logoUrl}
                className="agencyLogo"
                style={{ background: agency.backgroundColor }}
              />
            </div>
          ) : (
            <PlaceHolderImage size={60} title={agency.name} />
          )}
        </td>
        <td
          width={'50%'}
          data-testid={`agencyName-${slugify(agency?.name ?? '')}-${index}`}
        >
          <AgxHeader size={4}>{agency.name}</AgxHeader>
        </td>
        <td width={'25%'}>
          <StateChips
            states={
              agency.states?.map(
                (state) => state as unknown as LicenceDetailsState
              ) ?? []
            }
          />
        </td>
        <td>
          <AgencyPartnershipLevel
            partnershipLevel={
              (agency?.partnershipDetails
                ?.partnershipLevel as unknown as PartnershipDetailsPartnershipLevel) ??
              PartnershipDetailsPartnershipLevel.Silver
            }
          />
        </td>
      </tr>
    )
  }
)

AgencyRow.displayName = 'AgencyRow'

const StateChips = memo(({ states }: { states: LicenceDetailsState[] }) => {
  const sortedStateKeys = useMemo(() => states.sort(), [states])

  return (
    <AgxRow largeGap>
      {sortedStateKeys.map((stateKey) => (
        <div className="chip" key={stateKey}>
          {stateKey}
        </div>
      ))}
    </AgxRow>
  )
})

StateChips.displayName = 'StateChips'

const AgencyPartnershipLevel = memo(
  ({
    partnershipLevel,
  }: {
    partnershipLevel: PartnershipDetailsPartnershipLevel
  }) => {
    const { className, label, color } = useMemo(() => {
      let className = 'bronzePartnerStyle'
      let label = 'Bronze Partner'
      const color =
        partnershipLevel === PartnershipDetailsPartnershipLevel.XPartner
          ? '#FFFFFF'
          : '#000000'

      switch (partnershipLevel) {
        case PartnershipDetailsPartnershipLevel.Silver: {
          label = 'Silver Partner'
          className = 'silverPartnerStyle'
          break
        }
        case PartnershipDetailsPartnershipLevel.Gold: {
          label = 'Gold Partner'
          className = 'goldPartnerStyle'
          break
        }
        case PartnershipDetailsPartnershipLevel.Platinum: {
          label = 'Platinum Partner'
          className = 'platinumPartnerStyle'
          break
        }
        case PartnershipDetailsPartnershipLevel.XPartner: {
          label = 'X Partner'
          className = 'xPartnerStyle'
          break
        }
        case PartnershipDetailsPartnershipLevel.Bronze: {
          label = 'Bronze Partner'
          className = 'bronzePartnerStyle'
          break
        }
      }

      return { className, label, color }
    }, [partnershipLevel])

    return (
      <AgxRow justifyRight>
        <AgxRow extraClasses={`partnershipTag ${className}`} largeGap centered>
          <AgxPartnershipIcon
            height="20"
            width="20"
            color={color}
            partnershipLevel={partnershipLevel}
          />
          <AgxLabel
            medium
            colour={
              partnershipLevel === PartnershipDetailsPartnershipLevel.XPartner
                ? AgxColours.URBANX_WHITE
                : AgxColours.URBANX_BLACK
            }
          >
            {label}
          </AgxLabel>
        </AgxRow>
      </AgxRow>
    )
  }
)

AgencyPartnershipLevel.displayName = 'AgencyPartnershipLevel'

export default AgenciesPage
